import { useState, useEffect, useCallback, useMemo } from 'react';
import './BookingModal.css';
import { sendBookingEmail } from '../utils/emailService';
import { getErrorMessage } from '../utils/errorMessages';
import { calculateTotalPrice, formatPrice } from '../utils/pricingCalculator';
import { isInServiceArea, isValidZipFormat } from '../utils/serviceAreaValidator';
import VehicleSelector from './VehicleSelector';
import { trackBookingConversion } from '../utils/gtag';
import { fetchPricingConfig, getBundledConfig } from '../utils/pricingConfigClient';

const DEFAULT_ADD_ONS = getBundledConfig().add_ons || [
  { id: 'wiper-blades', name: 'Wiper Blade Replacement (pair)', duration: '5 min', price: 35 },
  { id: 'cabin-filter', name: 'Cabin Air Filter', duration: '5 min', price: 35 },
  { id: 'engine-filter', name: 'Engine Air Filter', duration: '5 min', price: 35 },
  { id: 'tire-rotation', name: 'Tire Rotation', duration: '15 min', price: 35 }
];

/**
 * BookingModal Component
 * Section 5 Implementation: Restructured booking flow
 *
 * Flow Order (per Section 5.1):
 * Step 1: Select Date & Time
 * Step 2: Vehicle Information (with Oil Specs displayed BEFORE payment)
 * Step 3: Customer Information
 * Step 4: Review & Payment
 * Completion: Confirmation
 *
 * Note: Service Type is pre-selected before modal opens
 */
function BookingModal({ service, onClose, isAdmin = false }) {
  // Jump start: 3-step flow (date → contact → review/pay)
  // Oil change: 4-step flow (date → vehicle → contact → review/pay)
  const isJumpStart = service.category === 'jump_start';
  const [step, setStep] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [serviceAreaError, setServiceAreaError] = useState(null);

  // Form data - now includes category from VehicleSelector
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    // Vehicle fields - populated by VehicleSelector
    serviceCategory: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleEngine: '',
    vehicleColor: '',
    // Oil specs from VehicleSelector
    oilType: '',
    oilCapacityQts: '',
    drainPlugTorque: '',
    oilFilterOem: '',
    filterWix: '',
    filterFram: '',
    filterKn: '',
    wheelTorque: '',
    // Other
    paymentMethod: '',
    message: ''
  });

  // Vehicle selection state
  const [vehicleData, setVehicleData] = useState(null);
  const [customColor, setCustomColor] = useState('');

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');

  // Category-based pricing
  const priceByCategory = {
    standard: 125,
    european: 125,
    truck: 150,
    jump_start: 99
  };

  const categoryLabels = {
    standard: 'Standard Oil Change',
    european: 'European Oil Change',
    truck: 'Light-Duty Trucks Oil Change',
    jump_start: 'Emergency Jump Start'
  };

  // Add-ons for oil change services — loaded from /api/pricing with bundled fallback
  const [addOns, setAddOns] = useState(DEFAULT_ADD_ONS);
  useEffect(() => {
    let cancelled = false;
    fetchPricingConfig().then(cfg => {
      if (!cancelled && Array.isArray(cfg.add_ons) && cfg.add_ons.length > 0) {
        setAddOns(cfg.add_ons);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const paymentMethods = [
    { id: 'paypal', label: 'PayPal' },
    { id: 'venmo', label: 'Venmo' },
    { id: 'zelle', label: 'Zelle' },
    { id: 'cash', label: 'Cash' }
  ];

  const vehicleColors = [
    'N/A', 'White', 'Black', 'Silver', 'Gray', 'Red', 'Blue', 'Brown', 'Green',
    'Beige', 'Orange', 'Gold', 'Yellow', 'Purple', 'Other'
  ];

  // API URL - use relative path for Vercel deployment
  const API_URL = '/api/bookings';

  // Fetch booked slots from API on mount
  const fetchBookedSlots = useCallback(async () => {
    try {
      const response = await fetch(API_URL);
      if (response.ok) {
        const data = await response.json();
        setBookedSlots(data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching booked slots:', error);
      // Fallback to localStorage if API fails
      const localBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      setBookedSlots(localBookings.map(b => ({ date: b.date, time: b.time })));
    }
  }, []);

  useEffect(() => {
    fetchBookedSlots();
  }, [fetchBookedSlots]);

  const toggleAddOn = (addOnId) => {
    if (selectedAddOns.includes(addOnId)) {
      setSelectedAddOns(selectedAddOns.filter(id => id !== addOnId));
    } else {
      setSelectedAddOns([...selectedAddOns, addOnId]);
    }
  };

  // Section 5.4: Detailed Pricing Integration
  // Build pricing input from vehicle data for pricingCalculator
  const buildPricingInput = useCallback(() => {
    if (!vehicleData?.specs) return null;

    return {
      oil_capacity_qts: (vehicleData.specs?._capacity_verified !== false
        ? vehicleData.specs?.oil_capacity_qts : null) || 5,
      oil_type: vehicleData.specs.oil_type || '',
      brand_category: vehicleData.category || 'standard',
      make: vehicleData.make || '',
      model: vehicleData.model || '',
      engine_code: vehicleData.engine || ''
    };
  }, [vehicleData]);

  // Calculate detailed pricing using pricingCalculator (memoized)
  const priceBreakdown = useMemo(() => {
    if (isJumpStart) {
      return { package_price: service.price, final_price: service.price, is_jump_start: true, is_fallback: true };
    }
    const pricingInput = buildPricingInput();

    // Fallback to package price if vehicle data incomplete
    if (!pricingInput || !pricingInput.oil_capacity_qts) {
      const category = vehicleData?.category || service.category || 'standard';
      return {
        package_price: priceByCategory[category] || service.price,
        final_price: priceByCategory[category] || service.price,
        oil_cost: 0,
        filter_price: 0,
        labor_price: 0,
        fees_total: 0,
        overage_charge: 0,
        is_fallback: true
      };
    }

    // Use real pricing calculator
    return calculateTotalPrice(pricingInput);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildPricingInput, vehicleData, service]); // priceByCategory is static

  // Get base price (package price or calculated)
  const getBasePrice = () => {
    return priceBreakdown.final_price || priceBreakdown.package_price || service.price;
  };

  // Calculate total with add-ons
  const calculateTotal = () => {
    if (isJumpStart) return service.price;
    let total = getBasePrice();
    selectedAddOns.forEach(addOnId => {
      const addOn = addOns.find(a => a.id === addOnId);
      if (addOn) total += addOn.price;
    });
    return total;
  };

  const generateCalendar = () => {
    const firstDay = new Date(displayYear, displayMonth, 1);
    const lastDay = new Date(displayYear, displayMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const getDayOfWeek = (day) => {
    if (!day) return '';
    const date = new Date(displayYear, displayMonth, day);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const today = new Date();

  // Maximum booking date: 60 days from today
  const maxBookingDate = new Date(today);
  maxBookingDate.setDate(maxBookingDate.getDate() + 60);

  // Check if we should show next month
  const getInitialMonth = () => {
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const isLastDayOfMonth = today.getDate() === lastDayOfMonth;
    const isAfter3pm = today.getHours() >= 15;

    if (isLastDayOfMonth && isAfter3pm) {
      return today.getMonth() === 11 ? 0 : today.getMonth() + 1;
    }
    return today.getMonth();
  };

  const getInitialYear = () => {
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const isLastDayOfMonth = today.getDate() === lastDayOfMonth;
    const isAfter3pm = today.getHours() >= 15;

    if (isLastDayOfMonth && isAfter3pm && today.getMonth() === 11) {
      return today.getFullYear() + 1;
    }
    return today.getFullYear();
  };

  const [displayMonth, setDisplayMonth] = useState(getInitialMonth());
  const [displayYear, setDisplayYear] = useState(getInitialYear());
  const currentMonth = monthNames[displayMonth];
  const currentYear = displayYear;

  const goToPrevMonth = () => {
    const initialMonth = getInitialMonth();
    const initialYear = getInitialYear();
    if (displayMonth === initialMonth && displayYear === initialYear) {
      return;
    }
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const isNextMonthDisabled = () => {
    const nextMonth = displayMonth === 11 ? 0 : displayMonth + 1;
    const nextYear = displayMonth === 11 ? displayYear + 1 : displayYear;
    const firstDayNextMonth = new Date(nextYear, nextMonth, 1);
    return firstDayNextMonth > maxBookingDate;
  };

  const goToNextMonth = () => {
    if (isNextMonthDisabled()) {
      setDisplayMonth(getInitialMonth());
      setDisplayYear(getInitialYear());
      setSelectedDate(null);
      setSelectedTime(null);
      return;
    }
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const isDayBeyondLimit = (day) => {
    if (!day) return false;
    const dateToCheck = new Date(displayYear, displayMonth, day);
    return dateToCheck > maxBookingDate;
  };

  // Hourly time slots for regular users
  const hourlyTimeSlots = [
    '8:00 am', '9:00 am', '10:00 am', '11:00 am',
    '12:00 pm', '1:00 pm', '2:00 pm', '3:00 pm', '4:00 pm', '5:00 pm', '6:00 pm', '7:00 pm', '8:00 pm'
  ];

  // 30-minute slots for admin
  const halfHourTimeSlots = [
    '8:00 am', '8:30 am', '9:00 am', '9:30 am', '10:00 am', '10:30 am', '11:00 am', '11:30 am',
    '12:00 pm', '12:30 pm', '1:00 pm', '1:30 pm', '2:00 pm', '2:30 pm', '3:00 pm', '3:30 pm',
    '4:00 pm', '4:30 pm', '5:00 pm', '5:30 pm', '6:00 pm', '6:30 pm', '7:00 pm', '7:30 pm', '8:00 pm'
  ];

  const timeSlots = isAdmin ? halfHourTimeSlots : hourlyTimeSlots;

  const getPrevSlots = (time, count) => {
    const slotIndex = timeSlots.indexOf(time);
    const result = [];
    for (let i = 1; i <= count && slotIndex - i >= 0; i++) {
      result.push(timeSlots[slotIndex - i]);
    }
    return result;
  };

  const parseTimeSlot = (time) => {
    const timeParts = time.match(/(\d+):(\d+)\s*(am|pm)/i);
    if (!timeParts) return null;

    let hours = parseInt(timeParts[1]);
    const minutes = parseInt(timeParts[2]);
    const isPM = timeParts[3].toLowerCase() === 'pm';

    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;

    return { hours, minutes };
  };

  const getSlotDateTime = (day, time) => {
    const parsed = parseTimeSlot(time);
    if (!parsed) return null;
    return new Date(displayYear, displayMonth, day, parsed.hours, parsed.minutes);
  };

  const isTimeSlotPast = (day, time) => {
    const slotDateTime = getSlotDateTime(day, time);
    if (!slotDateTime) return true;

    // Admin can book any time (only block slots already passed)
    if (isAdmin) {
      return slotDateTime <= today;
    }

    const slotHour = parseTimeSlot(time).hours;
    // Minimum advance: 8 hours for 8am slot, 2 hours for all others
    const minHoursAhead = slotHour === 8 ? 8 : 2;
    const cutoff = new Date(today.getTime() + minHoursAhead * 60 * 60 * 1000);

    return slotDateTime <= cutoff;
  };

  const isTimeSlotBooked = (day, time) => {
    if (isTimeSlotPast(day, time)) return true;

    // Weekday restriction: before 11 AM for regular users (not admin)
    if (!isAdmin) {
      const slotDate = new Date(displayYear, displayMonth, day);
      const dayOfWeek = slotDate.getDay(); // 0=Sun, 6=Sat
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const parsed = parseTimeSlot(time);
        if (parsed.hours < 11) return true;
      }
    }

    const dateStr = `${monthNames[displayMonth]} ${day}, ${displayYear}`;

    const isDirectlyBooked = bookedSlots.some(booking =>
      booking.date === dateStr && booking.time === time
    );
    if (isDirectlyBooked) return true;

    // Cascade block: admin books 1.5hr (3 half-hour slots), regular books 1hr (1 prev slot)
    if (!isAdmin) {
      // Regular user: block if previous hour slot is booked
      const prevSlots = getPrevSlots(time, 1);
      for (const prev of prevSlots) {
        if (bookedSlots.some(b => b.date === dateStr && b.time === prev)) return true;
      }
    } else {
      // Admin: block if any of previous 2 half-hour slots are booked (1.5hr total)
      const prevSlots = getPrevSlots(time, 2);
      for (const prev of prevSlots) {
        if (bookedSlots.some(b => b.date === dateStr && b.time === prev)) return true;
      }
    }

    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate ZIP code against service area whitelist (skip for admin)
    if (name === 'zipCode') {
      if (!isAdmin && value.length === 5 && isValidZipFormat(value)) {
        const result = isInServiceArea(value);
        if (!result.allowed) {
          setServiceAreaError('We are not providing service in that area yet. Please call (916) 818-8494 for questions.');
        } else {
          setServiceAreaError(null);
        }
      } else {
        setServiceAreaError(null);
      }
    }
  };

  // Handle vehicle selection from VehicleSelector (Phase 9.1)
  const handleVehicleSelect = (data) => {
    setVehicleData(data);
    setFormData(prev => ({
      ...prev,
      serviceCategory: data.category,
      vehicleYear: data.year,
      vehicleMake: data.make,
      vehicleModel: data.model,
      vehicleEngine: data.engine,
      oilType: data.specs?.oil_type || '',
      oilCapacityQts: data.specs?._capacity_verified !== false
        ? (data.specs?.oil_capacity_qts || '') : '',
      drainPlugTorque: data.specs?.drain_plug_torque_ft_lbs || '',
      oilFilterOem: data.specs?.filter_oem || '',
      filterWix: data.specs?.filter_wix || '',
      filterFram: data.specs?.filter_fram || '',
      filterKn: data.specs?.filter_kn || '',
      wheelTorque: data.specs?.wheel_torque_ft_lbs || ''
    }));
  };

  // Handle color selection
  const handleColorSelect = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, vehicleColor: value });
    if (value !== 'Other') {
      setCustomColor('');
    }
  };

  const getActualColor = () => {
    return formData.vehicleColor === 'Other' ? customColor : formData.vehicleColor;
  };

  const generateConfirmationNumber = () => {
    const prefix = 'LL';
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}-${timestamp}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const confNumber = generateConfirmationNumber();
    const actualColor = getActualColor();
    const basePrice = getBasePrice();

    // Build booking object with all vehicle data (Phase 9.3)
    const booking = {
      ...formData,
      confirmationNumber: confNumber,
      service: isJumpStart ? 'Emergency Jump Start' : (categoryLabels[formData.serviceCategory] || service.title),
      serviceCategory: isJumpStart ? 'jump_start' : formData.serviceCategory,
      service_type: isJumpStart ? 'jump_start' : 'oil_change',
      is_emergency: isJumpStart,
      vehicle_required: !isJumpStart,
      basePrice: basePrice,
      addOns: isJumpStart ? [] : selectedAddOns.map(id => addOns.find(a => a.id === id)),
      totalPrice: calculateTotal(),
      date: `${currentMonth} ${selectedDate}, ${currentYear}`,
      time: selectedTime,
      vehicleYear: formData.vehicleYear,
      vehicleMake: formData.vehicleMake,
      vehicleModel: formData.vehicleModel,
      vehicleEngine: isJumpStart ? '' : formData.vehicleEngine,
      vehicleColor: isJumpStart ? '' : actualColor,
      vehicleInfo: isJumpStart
        ? (formData.vehicleYear ? `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}`.trim() : 'Not provided')
        : `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel} (${formData.vehicleEngine})`,
      // Oil specs for technician (not applicable for jump start)
      oilType: isJumpStart ? '' : formData.oilType,
      oilCapacityQts: isJumpStart ? '' : formData.oilCapacityQts,
      capacityVerified: isJumpStart || vehicleData?.specs?._capacity_verified !== false,
      drainPlugTorque: isJumpStart ? '' : formData.drainPlugTorque,
      oilFilterOem: isJumpStart ? '' : formData.oilFilterOem,
      filterWix: isJumpStart ? '' : formData.filterWix,
      filterFram: isJumpStart ? '' : formData.filterFram,
      filterKn: isJumpStart ? '' : formData.filterKn,
      wheelTorque: isJumpStart ? '' : formData.wheelTorque,
      paymentMethod: paymentMethods.find(p => p.id === formData.paymentMethod)?.label || formData.paymentMethod,
      bookedAt: new Date().toISOString(),
      isAdmin: isAdmin || false
    };

    console.log('Booking submitted:', booking);

    try {
      // Save to API
      const apiResponse = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(booking)
      });

      if (!apiResponse.ok) {
        let errorData = {};
        try {
          errorData = await apiResponse.json();
        } catch {
          throw new Error(getErrorMessage('SERVER_ERROR'));
        }

        if (apiResponse.status === 409) {
          setSubmitError(getErrorMessage('SLOT_UNAVAILABLE'));
          setIsSubmitting(false);
          await fetchBookedSlots();
          setSelectedTime(null);
          setStep(1); // Go back to date/time selection
          return;
        }

        // Service area rejection from backend
        if (errorData.code === 'OUTSIDE_SERVICE_AREA') {
          setSubmitError(getErrorMessage('OUTSIDE_SERVICE_AREA'));
          setIsSubmitting(false);
          setStep(isJumpStart ? 2 : 3); // Go back to contact info to fix ZIP
          return;
        }

        // Use user-friendly error message
        const errorCode = errorData.code || 'SERVER_ERROR';
        throw new Error(getErrorMessage(errorCode, errorData.error));
      }

      setConfirmationNumber(confNumber);

      // Send booking to n8n (single request — n8n handles both business + customer emails)
      const emailResult = await sendBookingEmail(booking);
      if (emailResult.success) {
        console.log('Booking sent to automation successfully');
      } else {
        console.error('Failed to send booking to automation:', emailResult.error);
      }

      // Save to localStorage as backup
      const existingBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      existingBookings.push(booking);
      localStorage.setItem('bookings', JSON.stringify(existingBookings));

      setSubmitted(true);

      // Track Google Ads conversion
      trackBookingConversion(booking);

      setTimeout(() => {
        onClose();
      }, 5000);

    } catch (error) {
      console.error('Booking error:', error);
      setSubmitError(error.message || getErrorMessage('UNKNOWN'));
      setIsSubmitting(false);
    }
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    return `${getDayOfWeek(selectedDate)}, ${currentMonth} ${selectedDate}`;
  };

  const canProceedToStep2 = selectedDate && selectedTime;

  // Vehicle must be fully selected with category + color (oil change Step 2 → Step 3)
  const hasVehicle = vehicleData && vehicleData.category && vehicleData.year &&
                     vehicleData.make && vehicleData.model && vehicleData.engine;
  const hasColor = formData.vehicleColor && (formData.vehicleColor !== 'Other' || customColor.trim());

  // Customer info validation (phone optional for admin, required 10 digits for regular users)
  const phoneValid = isAdmin ? true : (formData.phone && formData.phone.replace(/\D/g, '').length === 10);
  const hasCustomerInfo = formData.name && formData.email &&
                          phoneValid && formData.address && formData.city && formData.zipCode;
  const zipIsValid = isAdmin
    ? (formData.zipCode && isValidZipFormat(formData.zipCode))
    : (formData.zipCode && isValidZipFormat(formData.zipCode) && isInServiceArea(formData.zipCode).allowed);

  // Step 2 → Step 3 gate
  // Jump start: Step 2 is contact/location, needs customer info + valid ZIP
  // Oil change: Step 2 is vehicle info, needs vehicle + color
  const canProceedToStep3 = isJumpStart
    ? (hasCustomerInfo && zipIsValid && (isAdmin || !serviceAreaError))
    : (hasVehicle && hasColor);

  // Step 3 → Step 4 gate (oil change only)
  const canProceedToStep4 = hasCustomerInfo && zipIsValid && (isAdmin || !serviceAreaError);

  // Final submission requires terms + payment
  const canSubmit = agreedToTerms && hasCustomerInfo && formData.paymentMethod;

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {!submitted ? (
          <>
            {/* STEP 1: Select Date & Time */}
            {step === 1 && (
              <div className="calendar-step">
                <h2>{service.title}</h2>
                <p className="service-price-header">From ${service.price} · {service.duration}</p>
                {isAdmin && <p className="admin-badge-booking">ADMIN MODE — All time slots available</p>}

                <div className="booking-content">
                  <div className="calendar-section">
                    <h3>Select a Date and Time</h3>
                    <p className="timezone">Pacific Standard Time (PST)</p>

                    <div className="calendar">
                      <div className="calendar-header">
                        <button
                          type="button"
                          className="month-nav-btn"
                          onClick={goToPrevMonth}
                          disabled={displayMonth === today.getMonth() && displayYear === today.getFullYear()}
                        >
                          ←
                        </button>
                        <span>{currentMonth} {currentYear}</span>
                        <button type="button" className="month-nav-btn" onClick={goToNextMonth}>
                          →
                        </button>
                      </div>
                      <div className="calendar-days-header">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="day-header">{day}</div>
                        ))}
                      </div>
                      <div className="calendar-grid">
                        {generateCalendar().map((day, index) => {
                          const isCurrentMonth = displayMonth === today.getMonth() && displayYear === today.getFullYear();
                          const isPastDay = isCurrentMonth && day && day < today.getDate();
                          const isTodayButTooLate = isCurrentMonth && day === today.getDate() && today.getHours() >= 17;
                          const isBeyondLimit = isDayBeyondLimit(day);
                          const isPast = isPastDay || isTodayButTooLate || isBeyondLimit;
                          const isSelectable = day && !isPast;
                          return (
                            <div
                              key={index}
                              className={`calendar-day ${day === null ? 'empty' : ''} ${
                                day && day === selectedDate ? 'selected' : ''
                              } ${isPast ? 'past' : ''}`}
                              onClick={() => {
                                if (isSelectable) {
                                  setSelectedDate(day);
                                  setSelectedTime(null);
                                }
                              }}
                            >
                              {day}
                              {isSelectable && <div className="availability-dot"></div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {selectedDate && (
                      <div className="time-slots-section">
                        <h4>Availability for {formatSelectedDate()}</h4>
                        <div className="time-slots-grid">
                          {timeSlots.map(time => {
                            const isBooked = isTimeSlotBooked(selectedDate, time);
                            return (
                              <button
                                key={time}
                                type="button"
                                className={`time-slot ${selectedTime === time ? 'selected' : ''} ${isBooked ? 'booked' : ''}`}
                                onClick={() => !isBooked && setSelectedTime(time)}
                                disabled={isBooked}
                              >
                                {time}
                                {isBooked && <span className="booked-label">Booked</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="details-section">
                    <h3>Service Details</h3>
                    <div className="service-summary-box">
                      <div className="summary-service-name">{service.title}</div>
                      <div className="summary-service-price">${service.price}</div>
                    </div>

                    {!isJumpStart && (
                      <div className="addons-inline">
                        <h4>Optional Add-ons</h4>
                        <div className="add-ons-list-compact">
                          {addOns.map(addOn => (
                            <div key={addOn.id} className="add-on-item-compact">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={selectedAddOns.includes(addOn.id)}
                                  onChange={() => toggleAddOn(addOn.id)}
                                />
                                <span className="addon-name">{addOn.name}</span>
                                <span className="addon-price">+${addOn.price}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="booking-summary">
                      {selectedDate && (
                        <div className="summary-item">
                          <strong>Date:</strong> {formatSelectedDate()}
                        </div>
                      )}
                      {selectedTime && (
                        <div className="summary-item">
                          <strong>Time:</strong> {selectedTime}
                        </div>
                      )}
                      <div className="summary-item total">
                        <strong>{isJumpStart ? 'Total:' : 'Estimated Total:'}</strong> ${calculateTotal()}
                      </div>
                      {!isJumpStart && (
                        <p className="price-disclaimer">
                          * Final price based on vehicle type. Standard: $125, European: $125, Trucks: $150
                        </p>
                      )}
                    </div>

                    {canProceedToStep2 && (
                      <button
                        className="btn-next"
                        onClick={() => setStep(2)}
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>

                {canProceedToStep2 && (
                  <div className="mobile-bottom-cta">
                    <button
                      className="btn-next btn-next-bottom"
                      onClick={() => setStep(2)}
                    >
                      {isJumpStart ? 'Continue to Location' : 'Continue to Vehicle Info'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Vehicle Information (oil change) OR Contact/Location (jump start) */}
            {step === 2 && !isJumpStart && (
              <div className="vehicle-step">
                <button className="btn-back" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Back
                </button>

                <h2>Vehicle Information</h2>
                <p className="step-subtitle">Select your vehicle to see the correct price and oil specifications</p>

                <div className="form-section">
                  {/* VehicleSelector Component - shows oil specs panel */}
                  <VehicleSelector
                    onVehicleSelect={handleVehicleSelect}
                    category={service.category}
                    initialData={vehicleData}
                  />

                  {/* Price breakdown based on vehicle specs (Section 5.4) */}
                  {vehicleData?.category && (
                    <div className={`category-price-indicator ${vehicleData.category}`}>
                      <div className="price-badge">
                        <span className="category-label">{categoryLabels[vehicleData.category]}</span>
                        <span className="category-price">{formatPrice(getBasePrice())}</span>
                      </div>
                      {/* Detailed breakdown when specs available */}
                      {!priceBreakdown.is_fallback && vehicleData.specs && (
                        <div className="price-breakdown-mini">
                          <div className="breakdown-row">
                            <span>Base Package</span>
                            <span>{formatPrice(priceBreakdown.base_price)}</span>
                          </div>
                          <div className="breakdown-info">
                            <small>Includes up to {priceBreakdown.baseline_capacity} qts oil + filter + labor</small>
                          </div>
                          {priceBreakdown.oil_surcharge > 0 && (
                            <>
                              <div className="breakdown-row overage">
                                <span>Oil Surcharge</span>
                                <span>+{formatPrice(priceBreakdown.oil_surcharge)}</span>
                              </div>
                              <div className="breakdown-info">
                                <small>
                                  Your vehicle needs {priceBreakdown.vehicle_capacity}qts
                                  ({priceBreakdown.surcharge_note})
                                </small>
                              </div>
                            </>
                          )}
                          {priceBreakdown.estimated && (
                            <div className="breakdown-info warning">
                              <small>{priceBreakdown.message}</small>
                            </div>
                          )}
                          {priceBreakdown.warning && (
                            <div className="breakdown-info warning">
                              <small>{priceBreakdown.warning}</small>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vehicle Color - required field */}
                  {vehicleData?.engine && (
                    <div className="form-row" style={{ marginTop: '20px' }}>
                      <div className="form-group">
                        <label htmlFor="vehicleColor">Vehicle Color *</label>
                        <select
                          id="vehicleColor"
                          name="vehicleColor"
                          value={formData.vehicleColor}
                          onChange={handleColorSelect}
                          required
                        >
                          <option value="">Select color...</option>
                          {vehicleColors.map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                        {formData.vehicleColor === 'Other' && (
                          <input
                            type="text"
                            className="custom-input"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            placeholder="Enter your vehicle color..."
                            required
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="step-summary">
                  <div className="summary-row">
                    <span>Service:</span>
                    <span>{vehicleData?.category ? categoryLabels[vehicleData.category] : service.title}</span>
                  </div>
                  <div className="summary-row">
                    <span>Date & Time:</span>
                    <span>{formatSelectedDate()} at {selectedTime}</span>
                  </div>
                  {vehicleData && (
                    <div className="summary-row">
                      <span>Vehicle:</span>
                      <span>{vehicleData.year} {vehicleData.make} {vehicleData.model}</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Estimated Total:</span>
                    <span>${calculateTotal()}</span>
                  </div>
                </div>

                <button
                  className="btn-next"
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                >
                  Continue to Contact Info
                </button>
              </div>
            )}

            {/* STEP 2 (Jump Start): Contact & Location */}
            {step === 2 && isJumpStart && (
              <div className="contact-info-step">
                <button className="btn-back" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Back
                </button>

                <h2>Where Are You?</h2>
                <p className="step-subtitle">Tell us where to find you and how to reach you</p>

                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number {isAdmin ? '(optional)' : '*'}</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required={!isAdmin} placeholder={isAdmin ? '' : '10-digit phone number'} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} placeholder="Where should we come?" required />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="zipCode">Zip Code *</label>
                      <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleChange} maxLength={5} pattern="\d{5}" placeholder="e.g. 95610" required />
                      {serviceAreaError && (
                        <div className="service-area-error" style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '6px', padding: '8px 12px', backgroundColor: '#ffeaea', borderRadius: '6px', border: '1px solid #f5c6c6' }}>
                          {serviceAreaError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Additional Notes</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="3" placeholder="Parking lot? Driveway? Any details to help us find you." />
                  </div>
                </div>

                <div className="step-summary">
                  <div className="summary-row">
                    <span>Service:</span>
                    <span>Emergency Jump Start</span>
                  </div>
                  <div className="summary-row">
                    <span>Date & Time:</span>
                    <span>{formatSelectedDate()} at {selectedTime}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>${service.price}</span>
                  </div>
                </div>

                <button
                  className="btn-next"
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                >
                  Continue to Review & Payment
                </button>
              </div>
            )}

            {/* STEP 3: Contact Information (oil change) OR Review & Payment (jump start) */}
            {step === 3 && !isJumpStart && (
              <div className="contact-info-step">
                <button className="btn-back" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Back
                </button>

                <h2>Contact Information</h2>
                <p className="step-subtitle">Where should we come for your service?</p>

                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Country/Region</label>
                    <div className="country-fixed">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      United States
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Where should we come?"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="zipCode">Zip Code *</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        maxLength={5}
                        pattern="\d{5}"
                        placeholder="e.g. 95610"
                        required
                      />
                      {serviceAreaError && (
                        <div className="service-area-error" style={{
                          color: '#d32f2f',
                          fontSize: '0.85rem',
                          marginTop: '6px',
                          padding: '8px 12px',
                          backgroundColor: '#ffeaea',
                          borderRadius: '6px',
                          border: '1px solid #f5c6c6'
                        }}>
                          {serviceAreaError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Additional Notes</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Any special requests or concerns?"
                    />
                  </div>
                </div>

                <button
                  className="btn-next"
                  onClick={() => setStep(4)}
                  disabled={!canProceedToStep4}
                >
                  Continue to Review & Payment
                </button>
              </div>
            )}

            {/* STEP 3 (Jump Start): Review & Payment */}
            {step === 3 && isJumpStart && (
              <div className="review-payment-step">
                <button className="btn-back" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Back
                </button>

                <h2>Review & Payment</h2>
                <p className="step-subtitle">Review your booking and select payment method</p>

                <div className="form-layout">
                  <div className="form-main">
                    {/* Optional vehicle info for jump start */}
                    <div className="form-section jump-start-vehicle-optional">
                      <h3>Vehicle Info (Optional)</h3>
                      <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '12px' }}>
                        Helps our technician prepare, but not required
                      </p>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="vehicleYear">Year</label>
                          <input type="text" id="vehicleYear" name="vehicleYear" value={formData.vehicleYear} onChange={handleChange} placeholder="e.g. 2020" maxLength={4} />
                        </div>
                        <div className="form-group">
                          <label htmlFor="vehicleMake">Make</label>
                          <input type="text" id="vehicleMake" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} placeholder="e.g. Toyota" />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="vehicleModel">Model</label>
                        <input type="text" id="vehicleModel" name="vehicleModel" value={formData.vehicleModel} onChange={handleChange} placeholder="e.g. Camry" />
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="form-section">
                      <h3>Payment Method (Pay after service) *</h3>
                      <div className="payment-options">
                        {paymentMethods.map(method => (
                          <label key={method.id} className={`payment-option ${formData.paymentMethod === method.id ? 'selected' : ''}`}>
                            <input type="radio" name="paymentMethod" value={method.id} checked={formData.paymentMethod === method.id} onChange={handleChange} required />
                            <span className="payment-label">{method.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Terms Agreement */}
                    <div className="form-section">
                      <div className="form-group terms-checkbox">
                        <label className="checkbox-label">
                          <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} required />
                          <span className="checkbox-text">
                            I agree to the{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>,{' '}
                            <a href="/cancellation" target="_blank" rel="noopener noreferrer">Cancellation Policy</a>, and{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                          </span>
                        </label>
                      </div>
                    </div>

                    {submitError && (
                      <div className="submit-error">{submitError}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <button type="submit" className="btn-book-now" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Processing...' : 'Complete Booking — $99'}
                      </button>
                    </form>
                  </div>

                  <div className="form-sidebar">
                    <h3>Booking Summary</h3>
                    <div className="booking-details-box">
                      <div className="detail-item">
                        <strong>Emergency Jump Start</strong>
                      </div>
                      <div className="detail-item">
                        {formatSelectedDate()} at {selectedTime}
                      </div>

                      <div className="detail-section">
                        <h4>Location</h4>
                        <div className="detail-text">{formData.name}</div>
                        <div className="detail-text">{formData.phone}</div>
                        <div className="detail-text">{formData.address}</div>
                        <div className="detail-text">{formData.city}, {formData.zipCode}</div>
                      </div>

                      {formData.vehicleYear && (
                        <div className="detail-section">
                          <h4>Vehicle</h4>
                          <div className="detail-text">
                            {formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}
                          </div>
                        </div>
                      )}

                      <div className="detail-section total-section">
                        <div className="price-row total">
                          <span>Total</span>
                          <span className="price-amount">{formatPrice(calculateTotal())}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Review & Payment — oil change only */}
            {step === 4 && !isJumpStart && (
              <div className="review-payment-step">
                <button className="btn-back" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Back
                </button>

                <h2>Review & Payment</h2>
                <p className="step-subtitle">Review your booking details and select payment method</p>

                <div className="form-layout">
                  <div className="form-main">
                    {/* Payment Method Selection */}
                    <div className="form-section">
                      <h3>Payment Method (Pay after service) *</h3>
                      <div className="payment-options">
                        {paymentMethods.map(method => (
                          <label key={method.id} className={`payment-option ${formData.paymentMethod === method.id ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method.id}
                              checked={formData.paymentMethod === method.id}
                              onChange={handleChange}
                              required
                            />
                            <span className="payment-label">{method.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Terms Agreement */}
                    <div className="form-section">
                      <div className="form-group terms-checkbox">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            required
                          />
                          <span className="checkbox-text">
                            I agree to the{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>,{' '}
                            <a href="/cancellation" target="_blank" rel="noopener noreferrer">Cancellation Policy</a>, and{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                          </span>
                        </label>
                      </div>
                    </div>

                    {submitError && (
                      <div className="submit-error">
                        {submitError}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <button
                        type="submit"
                        className="btn-book-now"
                        disabled={!canSubmit || isSubmitting}
                      >
                        {isSubmitting ? 'Processing...' : 'Complete Booking'}
                      </button>
                    </form>
                  </div>

                  <div className="form-sidebar">
                    <h3>Booking Summary</h3>
                    <div className="booking-details-box">
                      <div className="detail-item">
                        <strong>{categoryLabels[formData.serviceCategory] || service.title}</strong>
                      </div>
                      <div className="detail-item">
                        {formatSelectedDate()} at {selectedTime}
                      </div>

                      <div className="detail-section">
                        <h4>Vehicle</h4>
                        <div className="detail-text">
                          {formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}
                        </div>
                        <div className="detail-text">
                          Engine: {formData.vehicleEngine}
                        </div>
                        <div className="detail-text">
                          Color: {getActualColor()}
                        </div>
                      </div>

                      {formData.oilType && (
                        <div className="detail-section">
                          <h4>Oil Specifications</h4>
                          <div className="detail-text">
                            {formData.oilType}
                          </div>
                          {formData.oilCapacityQts && (
                            <div className="detail-text">
                              Capacity: {formData.oilCapacityQts} qts
                            </div>
                          )}
                        </div>
                      )}

                      <div className="detail-section">
                        <h4>Contact</h4>
                        <div className="detail-text">{formData.name}</div>
                        <div className="detail-text">{formData.email}</div>
                        <div className="detail-text">{formData.phone}</div>
                        <div className="detail-text">{formData.address}</div>
                        <div className="detail-text">{formData.city}, {formData.zipCode}</div>
                      </div>

                      {selectedAddOns.length > 0 && (
                        <div className="detail-section">
                          <h4>Add-ons</h4>
                          {selectedAddOns.map(addOnId => {
                            const addOn = addOns.find(a => a.id === addOnId);
                            return (
                              <div key={addOnId} className="detail-addon">
                                {addOn.name} - ${addOn.price}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="detail-section total-section">
                        {/* Detailed price breakdown - New Baseline Model */}
                        {!priceBreakdown.is_fallback && !priceBreakdown.is_electric && (
                          <>
                            <div className="price-row breakdown">
                              <span>{categoryLabels[formData.serviceCategory]} Package</span>
                              <span>{formatPrice(priceBreakdown.base_price)}</span>
                            </div>
                            <div className="price-detail-info">
                              <small>Includes up to {priceBreakdown.baseline_capacity} qts oil, filter, labor & disposal</small>
                            </div>
                            {priceBreakdown.oil_surcharge > 0 && (
                              <>
                                <div className="price-row breakdown overage">
                                  <span>Oil Surcharge</span>
                                  <span>+{formatPrice(priceBreakdown.oil_surcharge)}</span>
                                </div>
                                <div className="price-detail-info">
                                  <small>
                                    Vehicle requires {priceBreakdown.vehicle_capacity}qts
                                    ({priceBreakdown.surcharge_note})
                                  </small>
                                </div>
                              </>
                            )}
                            <div className="price-row subtotal">
                              <span>Service Subtotal</span>
                              <span>{formatPrice(getBasePrice())}</span>
                            </div>
                          </>
                        )}
                        {priceBreakdown.is_fallback && (
                          <div className="price-row">
                            <span>{categoryLabels[formData.serviceCategory]}</span>
                            <span>{formatPrice(getBasePrice())}</span>
                          </div>
                        )}
                        {priceBreakdown.is_electric && (
                          <div className="price-detail-info warning">
                            <p>{priceBreakdown.message}</p>
                          </div>
                        )}
                        {selectedAddOns.length > 0 && (
                          <div className="price-row breakdown">
                            <span>Add-ons</span>
                            <span>{formatPrice(selectedAddOns.reduce((sum, id) => {
                              const addOn = addOns.find(a => a.id === id);
                              return sum + (addOn?.price || 0);
                            }, 0))}</span>
                          </div>
                        )}
                        <div className="price-row total">
                          <span>Total</span>
                          <span className="price-amount">{formatPrice(calculateTotal())}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2>Booking Confirmed!</h2>
            <p className="confirmation-number">Confirmation #: <strong>{confirmationNumber}</strong></p>
            <p>A confirmation email has been sent to <strong>{formData.email}</strong></p>

            <div className="confirmation-details">
              <div className="conf-row">
                <span>Service:</span>
                <span>{isJumpStart ? 'Emergency Jump Start' : (categoryLabels[formData.serviceCategory] || service.title)}</span>
              </div>
              <div className="conf-row">
                <span>Date:</span>
                <span>{formatSelectedDate()}</span>
              </div>
              <div className="conf-row">
                <span>Time:</span>
                <span>{selectedTime}</span>
              </div>
              {isJumpStart ? (
                <div className="conf-row">
                  <span>Location:</span>
                  <span>{formData.address}, {formData.city} {formData.zipCode}</span>
                </div>
              ) : (
                <>
                  <div className="conf-row">
                    <span>Vehicle:</span>
                    <span>{getActualColor()} {formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}</span>
                  </div>
                  <div className="conf-row">
                    <span>Engine:</span>
                    <span>{formData.vehicleEngine}</span>
                  </div>
                </>
              )}
              <div className="conf-row">
                <span>Payment:</span>
                <span>{paymentMethods.find(p => p.id === formData.paymentMethod)?.label}</span>
              </div>
              <div className="conf-row total">
                <span>Total:</span>
                <span>${calculateTotal()}</span>
              </div>
            </div>

            <p className="contact-note">
              We'll contact you at <strong>{formData.phone}</strong> to confirm your appointment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingModal;
